import axios from 'axios'
import Vue from 'vue'

/* eslint-disable max-len */
// Disabling max len in this entire file because of long queries

// see https://cli.vuejs.org/guide/mode-and-env.html
// env variables must start with VUE_APP_
var BACKEND_URL
if (process.env.VUE_APP_BACKEND === 'local') {
  BACKEND_URL = 'http://localhost:4000/'
} else if (process.env.VUE_APP_BACKEND === 'staging') {
  BACKEND_URL = 'http://api-tournament.staging.tournament.internal.numer.ai/'
} else {
  BACKEND_URL = 'https://api-tournament.numer.ai/'
}

if (process.env.NODE_ENV != 'production') {
  console.log('Using backend URL ' + BACKEND_URL)
}

// be ok with any status code
// axios will throw on !2XX by default
axios.defaults.validateStatus = function() {
  return true
}

// Set a custom 401 callback
var on401Callback = () => {
  localStorage.removeItem('auth-token')
}
function setOn401Callback(fn) {
  on401Callback = fn
}

var onGraphQLErrorCallback = () => {}
function setGraphQLErrorCallback(fn) {
  onGraphQLErrorCallback = fn
}

// wrap all api calls with auth and error handling to keep DRY
async function _post(query, variables, returnField = null, retries = 2) {
  var opts = {
    timeout: 20000,
  }

  // inject auth header if present
  if ('auth-token' in localStorage) {
    opts = {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('auth-token'),
      },
    }
  }

  // make the request
  const res = await axios.post(BACKEND_URL, { query, variables }, opts)

  if (res.status == 500) {
    throw new Error('API internal server error.')
  }

  // intercept 401 if custom callback is set
  if (res.status == 401) {
    on401Callback()
  }

  // turn first error message into an exception
  if (res.data.errors && res.data.errors.length > 0) {
    // treat graphql auth errors as 401
    const errorCode = res.data.errors[0].code
    if (errorCode == 'not_authenticated' || errorCode == 'session_expired') {
      on401Callback()
    }

    const errorMsg = res.data.errors[0].message

    if (errorMsg.includes('Cannot query field')) {
      console.error(errorMsg)
      onGraphQLErrorCallback()
      throw new Error('GraphQL error.')
    }
    console.error(errorMsg)
    throw new Error(errorMsg)
  }

  const data = res.data.data
  if (data === undefined && retries > 0) {
    console.log('API returned undefined data. Retrying...')
    return await _post(query, variables, returnField, retries - 1)
  } else if (data === undefined) {
    console.log('API returned undefined data.')
    return null
  }
  if (returnField !== null) {
    return data[returnField]
  } else {
    return data
  }
}

async function login({ email, password, code }) {
  const query = `
    mutation login($email: String!, $password: String!, $code: String) {
      login(email: $email, password: $password, code: $code) {
        username
        token
      }
    }
  `
  const res = await _post(query, { email, password, code })
  return res.login
}

async function logout() {
  const query = `
    mutation logout {
      logout
    }
  `
  const res = await _post(query)
  return res.logout
}

async function singleSignOn({ payload, signature }) {
  const query = `
    query($payload: String!, $signature: String!) {
      sso(payload: $payload, signature: $signature) {
        url
      }
    }
  `
  const res = await _post(query, { payload, signature })
  return res
}

async function signup({
  email,
  username,
  password,
  captcha,
  tournament,
  campaignId,
  source,
}) {
  const query = `
    mutation createUser(
      $email: String!,
      $username: String!,
      $password: String!,
      $captcha: String!,
      $tournament: Int!,
      $campaignId: String,
      $source: String
    ) {
      createUser(
      email: $email, 
      username: $username, 
      password: $password, 
      captcha: $captcha, 
      tournament: $tournament, 
      campaignId: $campaignId,
      source: $source
    ) {
        username
        email
      }
    }
  `
  const res = await _post(query, {
    email,
    username,
    password,
    captcha,
    tournament,
    campaignId,
    source,
  })
  return res.createUser
}

async function confirmSignup({ token }) {
  const query = `
    mutation confirmCreateUser($token: String!) {
      confirmCreateUser(token: $token) {
        username
        token
      }
    }
  `
  const res = await _post(query, { token })
  return res.confirmCreateUser
}

async function deleteAccount({ password, code }) {
  const query = `
    mutation deleteAccount($password: String!, $code: String!) {
      deleteAccount(password: $password, code: $code)
    }
  `
  const res = await _post(query, { password, code })
  return res.deleteAccount
}

async function getDefaultApiToken() {
  const query = `
    query defaultApiToken {
      defaultApiToken {
        name
        publicId
        secretKey
      }
    }
  `
  const res = await _post(query)
  return res.defaultApiToken
}

async function verifyEmail({ token }) {
  const query = `
    mutation verify($token: String!) {
      verifyEmail(token: $token)
    }
  `
  await _post(query, { token })
}

async function resetEmailChange({ token }) {
  const query = `
    mutation resetEmail($token: String!) {
      resetEmailChange(token: $token)
    }
  `
  await _post(query, { token })
}

async function verifyLoginIp({ token }) {
  const query = `
    mutation verifyLoginIp($token: String!) {
      verifyLoginIp(token: $token) {
        username
        token
      }
    }
  `
  const res = await _post(query, { token })
  return res.verifyLoginIp
}

async function resetPassword(args) {
  const query = `
    mutation resetPassword($email: String!) {
      resetPassword(email: $email)
    }
  `
  await _post(query, args)
}

async function confirmResetPassword({ token, newPassword }) {
  const query = `
    mutation resetPasswordFromToken($newPassword: String!, $token: String!) {
      resetPasswordFromToken(newPassword: $newPassword, token: $token)
    }
  `
  await _post(query, { token, newPassword })
}

async function absorbAccount({ token }) {
  const query = `
    mutation ($token: String!) {
      absorbAccount(token: $token)
    }
  `
  const res = await _post(query, { token })
  return res
}

async function addModel(name, tournament_number) {
  const query = `
    mutation addModel($name: String!, $tournament: Int!) {
      addModel(name: $name, tournament: $tournament) {
        id
        name
      }
    }
  `
  const res = await _post(query, {
    name: name,
    tournament: tournament_number || 8,
  })
  return res
}
async function renameModel(modelId, name) {
  const query = `
    mutation($modelId: String!, $name: String!) {
      renameModel(modelId: $modelId, name: $name) {
        name
      }
    }
  `
  const res = await _post(query, { modelId: modelId, name: name })
  return res
}

async function renameAccount(name) {
  const query = `
      mutation renameAccount($name: String!) {
        renameAccount(name: $name) {
          username
        }
      }
    `
  const res = await _post(query, { name })
  return res.renameAccount
}

async function modelNameAvailable(name, tournament) {
  const query = `
    query modelNameAvailable($name: String!, $tournament: Int!) {
      modelNameAvailable(name: $name, tournament: $tournament)
    }
  `
  const res = await _post(query, { name, tournament: tournament || 8 })
  return res.modelNameAvailable
}

async function getPrices() {
  const query = `
    query getPrices {
      latestNmrPrice {
        lastUpdated
        priceUsd
      }
      latestEthPrice {
        lastUpdated
        priceUsd
      }
    }
  `
  const res = await _post(query)
  return res
}

async function latestNmrPrice() {
  const query = `
    query latestNmrPrice {
      latestNmrPrice {
        lastUpdated
        priceUsd
      }
    }
  `
  return await _post(query, {}, 'latestNmrPrice')
}

async function getDataset(filename, tournament) {
  const query = `
  query dataset ($filename: String!, $tournament: Int!) {
    dataset(filename: $filename, tournament: $tournament)
  }
  `
  const res = await _post(query, { filename, tournament })
  return res.dataset
}

async function getOldDataset() {
  const query = `
  query dataset {
    dataset
  }
  `
  const res = await _post(query)
  return res.dataset
}

async function listDatasets() {
  const query = `
  query listDatasets{
    listDatasets
  }
  `
  const res = await _post(query)
  return res.listDatasets
}

async function getReport(id) {
  const query = `
    query getReport ($id: String!) {
      reportLink(id: $id)
    }
  `
  const res = await _post(query, { id: id })
  return res.reportLink
}

async function getSignalsSubmissionDiagnostics(submissionId) {
  const query = `
  query signalsSubmissionDiagnostics($submissionId: String!) {
    signalsSubmissionDiagnostics(submissionId: $submissionId) {
      corr
      date
    }
  }
  `
  const res = await _post(query, {
    submissionId: submissionId,
  })

  if (res.signalsSubmissionDiagnostics.length == 0) {
    return null
  }

  return res.signalsSubmissionDiagnostics
}

async function getAccount() {
  const query = `
    query getAccount_20231012 {
      account {
        email
        username
        mfaEnabled
        insertedAt
        isBeta
        walletAddress
        availableNmr
        availableStakeCredit
        heldForPendingWithdrawals
        heldForScheduledStakeIncreases
        discordLinkToken
        profileUrl
        emailPreferences {
          compute
          deposit
          diagnostics
          modelUploadReceipt
          pickleRoundOpen
          pickleRoundStatus
          roundOpen
          roundSummary
          signalsRoundOpen
          signalsRoundSummary
          signalsSubmission
          cryptoRoundOpen
          cryptoRoundSummary
          cryptoSubmission
          stakeChange
          submission
          submissionSuccess
          submissionsStatus
          withdrawal
        }
        models {
          id
          name
          description
          profileUrl
          earliestReleaseDate
          computePickleUpload {
            id
          }
          returns{
            oneDay
            threeMonths
            oneYear
            allTime
          }
          currentPayoutSelection {
            corrMultiplier
            mmcMultiplier
            tcMultiplier
            takeProfit
            updatedAt
          }
          tournament
          submissionWebhook
          computeEnabled
          isComputeWeekdayEnabled
          latestSubmissions(latestNRounds: 60) {
            id
            roundNumber
            roundOpen
            roundCloseStaking
            status
            filename
            timestamp
          }
          v2Stake {
            latestValue
            latestValueSettled
            stakeValue
            status
            pendingV2ChangeStakeRequest {
              requestedAmount
              status
              type
              dueDate
              drain
            }
          }
          latestUserScores {
            displayName
            reputation
          }
        }
        apiTokens {
          name
          publicId
          scopes
        }
        reports {
          id
          name
          key
          insertedAt
        }
      }
    }
  `
  const res = await _post(query)
  return res.account
}

async function getAccountProfile(accountName, tournament) {
  const query = `
    query accountProfile($username: String!, $tournament: Int) {
      accountProfile(username: $username, tournament: $tournament) {
          id
          username
          title
          achievements {
            date
            rank
            type
            season
            tier
          }
          tournament
          displayName
          bio
          github
          kaggle
          linkedin
          twitter
          discord {
            userId
            username
          }
          profileUrl
          location
          website
          startDate
          team
          scores {
            tcLtmRank
            v2Corr20LtmRank
            fncV4LtmRank
            mmcLtmRank
            corrLtmRank
            seasonRank
            corr60LtmRank
            mmc60LtmRank
            alphaLtmRank
            mpcLtmRank
          }
          scoresTs {
            date
            tc
            tcLtm
            v2Corr20
            v2Corr20Ltm
            fncV4
            fncV4Ltm
            mmc
            mmcLtm
            corr
            corrLtm
            corr60
            corr60Ltm
            mmc60
            mmc60Ltm
            alpha
            alphaLtm
            mpc
            mpcLtm
          }
          returns {
            allTime
            allTimeNmr
            oneYear
            oneYearNmr
          }
          returnsTs {
            allTime
            date
            oneYear
          }
          totalStake
          totalStakeTs {
            delta
            date
            value
          }
          models {
            displayName
            stake
            profileUrl
            return1y
            tcRep
            corr20V2Rep
            fncV4Rep
            mmcRep
            corrRep
            mmc60Rep
            corr60Rep
            alphaRep
            mpcRep
          }
        }
      }
    `
  const res = await _post(query, {
    username: accountName,
    tournament,
  })
  return res.accountProfile
}

async function getSeasonAccountSummary(tournament) {
  const query = `
    query SeasonAccountSummary($tournament: Int!) {
      seasonAccountSummary(tournament: $tournament) {
        year
        openDate
        closeStakingEnd
        resolveDate
        firstScoreDate
        hasScores
        roundsLeft
        hasStakedOneNmr
        status
        daysUntilNextSeasonOpen
        participants
        numQualifyingSubmissions
        titleInfo {
          title
          titleDescription
        }
        seasonAccountPerformance {
          rank
          scoreName
        }
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.seasonAccountSummary
}

async function getSeasonParticipants(tournament) {
  const query = `
    query SeasonAccountSummary($tournament: Int!) {
      seasonAccountSummary(tournament: $tournament) {
        year
        hasScores
        participants
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.seasonAccountSummary
}

async function getSeasonSummaryStatusData(tournament) {
  const query = `
    query SeasonSummaryStatusData($tournament: Int!) {
      seasonAccountSummary(tournament: $tournament) {
        year
        hasScores
        numQualifyingSubmissions
        firstScoreDate
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.seasonAccountSummary
}

async function getSeasonLeaderboard(seasonYear, tournament) {
  const query = `
    query SeasonLeaderboard($tournament: Int!, $seasonYear: Int!) {
      seasonLeaderboard(tournament: $tournament, seasonYear: $seasonYear, tierNumber__lte: 6) {
        seasonYear
        tiers {
          tierName
          tierNumber
          tierDescription
          leaderboard {
            accountDisplayName
            accountUsername
            numeraiTitle
            signalsTitle
            cryptosignalsTitle
            accountTc
            accountTcRank
            accountCorr
            accountCorrRank
            accountSeasonScore
            accountSeasonScoreRank
            profileUrl
            exclude_from_season
          }
        }
      }
    }
  `
  const res = await _post(query, { tournament, seasonYear })
  return res.seasonLeaderboard
}

async function updateAccountProfile(options) {
  const query = `
    mutation setAccountMeta($bio: String, $displayName: String, $github: String, $kaggle: String, $linkedin: String, $location: String, $occupation: String, $organization: String, $twitter: String, $website: String) {
      setAccountMeta(bio: $bio, displayName: $displayName, github: $github, kaggle: $kaggle, linkedin: $linkedin, location: $location, occupation: $occupation, organization: $organization, twitter: $twitter, website: $website)
    }
  `
  const res = await _post(query, { ...options })
  return res.setAccountMeta
}

async function nameCheck(username) {
  const query = `
    query accountNameAvailable($name: String!) {
      accountNameAvailable(name: $name)
    }
  `
  const res = await _post(query, { name: username })
  return res.accountNameAvailable
}

async function getTutorialCompletion(tournamentName) {
  const query = `
    query getTutorialCompletion {
      account {
        tutorials {
          numerai {
            helloNumerai
            targetEnsemble
            featureNeutralization
          }
        }
      }
    }
  `
  const res = await _post(query)
  return res.account.tutorials[tournamentName]
}

async function getAccountWalletTransactions() {
  const query = `
    query getAccountWalletTransactions {
      account {
        walletTxns {
          from
          to
          amount
          type
          status
          time
          txHash
          tournament
        }
      }
    }
  `
  const res = await _post(query)
  return res.account.walletTxns
}

async function getStakeTransactions(modelId) {
  const query = `
    query getStakeTransactions($modelId: String!) {
      stakeTransactions(modelId: $modelId) {
        amount
        model
        timestamp
        type
      }
    }
  `
  const res = await _post(query, { modelId })
  return res.stakeTransactions
}

async function getAccountWalletScheduledTransactions() {
  const query = `
    query getAccountWalletScheduledTransactions {
      account {
        scheduledStakeTxns {
          amount
          dueDate
          model
          status
          time
          tournament
          type
        }
      }
    }
  `
  const res = await _post(query)
  return res.account.scheduledStakeTxns
}

async function getAccountWalletPendingTransactions() {
  const query = `
    query getAccountWalletPendingTransactions{
      account {
        pendingTxns {
          from
          to
          amount
          type
          status
          time
          txHash
          tournament
        }
      }
    }
  `
  const res = await _post(query)
  return res.account.pendingTxns
}

async function getV3UserProfile({ username, tournament }) {
  /* cSpell:disable */
  const query = `
    query getV3UserProfile($username: String!, $tournament: Int!) {
      v3UserProfile(modelName: $username, tournament: $tournament) {
        id
        accountName
        username
        startDate
        profileUrl
        bio
        stakeValue
        control
        team
        isActive
        linkText
        linkUrl
        latestReps {
          corr
          corr20V2
          mmc
          bmc
          corr60
          mmc60
          corrV4
          fncV4
          icV2
          ric
          alpha
          mpc
        }
        latestRanks {
          corr
          corr20V2
          mmc
          bmc
          corr60
          mmc60
          corrV4
          fncV4
          icV2
          ric
          alpha
          mpc
        }
        latestReturns{
          oneDay
          threeMonths
          oneYear
          allTime
        }
        latestUserScores {
          displayName
          rank
          reputation
        }
        latestSubmissionScores {
          displayName
          value
          percentile
        }
        stakeInfo {
          corrMultiplier
          mmcMultiplier
          tcMultiplier
        }
      }
    }
  `
  /* cSpell:enable */
  const res = await _post(query, { username, tournament })
  return res.v3UserProfile
}

async function getUnreadNotifications() {
  const query = `
  query unreadNotifications {
    unreadNotifications {
      id
      bodyText
      ctaButtonLink
      ctaButtonText
      titleText
      data
    }
  }
  `
  return await _post(query, {}, 'unreadNotifications')
}

async function dismissNotification(notificationId) {
  const query = `
    mutation dismissNotification($notificationId: String!) {
      dismissNotification (notificationId: $notificationId)
    }
  `
  const res = await _post(query, { notificationId: notificationId })
  return res.dismissNotification
}

async function getUnreadBanners() {
  const query = `
    query getUnreadBanners {
      unreadBanners {
        expirationDate
        type
      }
    }
  `
  const res = await _post(query)
  return res.unreadBanners
}

async function markBannerAsRead(type) {
  const query = `
    mutation markBannerAsRead($type: String!) {
      markBannerAsRead (type: $type)
    }
  `
  const res = await _post(query, { type: type })
  return res.unreadBanners
}

async function getDiagnostics(id, modelId) {
  const query = `
    query getDiagnostics($id: Int, $modelId: Int) {
        diagnostics(id: $id, modelId: $modelId) {
            id
            insertedAt
            updatedAt
            status
            message
            tournament
            filename
            examplePredsCorrMean
            perEraDiagnostics {
              era
              validationCorr
              validationBmc
              validationFeatureNeutralCorrV3
              validationCorrV4
              validationIcV2
              validationRic
              validationFncV4
            }
            validationCorrMean
            validationCorrMaxDrawdown
            validationCorrV4Mean
            validationCorrV4Std
            validationCorrV4Sharpe
            validationCorrV4MaxDrawdown
            validationIcV2Mean
            validationIcV2Std
            validationIcV2Sharpe
            validationIcV2MaxDrawdown
            validationRicMean
            validationRicStd
            validationRicSharpe
            validationRicMaxDrawdown
            validationFncV4Mean
            validationFncV4Std
            validationFncV4Sharpe
            validationFncV4MaxDrawdown
            validationCorrMeanRating
            validationCorrPlusMmcMean
            validationCorrPlusMmcMeanRating
            validationCorrPlusMmcSharpe
            validationCorrPlusMmcSharpeDiff
            validationCorrPlusMmcSharpeDiffRating
            validationCorrPlusMmcSharpeRating
            validationCorrPlusMmcStd
            validationCorrPlusMmcStdRating
            validationCorrSharpe
            validationCorrSharpeRating
            validationCorrStd
            validationCorrStdRating
            validationFeatureCorrMax
            validationFeatureCorrMaxRating
            validationFeatureNeutralCorrMean
            validationFeatureNeutralCorrMeanRating
            validationFeatureNeutralCorrV3Mean
            validationFeatureNeutralCorrV3MeanRating
            validationMaxDrawdown
            validationMaxDrawdownRating
            validationMmcMean
            validationMmcMeanRating
            validationMmcSharpe
            validationMmcSharpeRating
            validationMmcStd
            validationMmcStdRating
            validationBmcMean
            validationApy
            validationAutocorr
            validationAdjustedSharpe
            validationChurnMean
            validationChurnStd
            validationChurnMax
            top_bottom
            tb200Diagnostics
            {
              id
              insertedAt
              updatedAt
              status
              message
              tournament
              filename
              examplePredsCorrMean
              perEraDiagnostics {
                era
                validationCorr
                validationFeatureNeutralCorrV3
                validationBmc
                validationCorrV4
                validationIcV2
                validationRic
                validationFncV4
              }
              validationCorrMean
              validationCorrMaxDrawdown
              validationCorrV4Mean
              validationCorrV4Std
              validationCorrV4Sharpe
              validationCorrV4MaxDrawdown
              validationIcV2Mean
              validationIcV2Std
              validationIcV2Sharpe
              validationIcV2MaxDrawdown
              validationRicMean
              validationRicStd
              validationRicSharpe
              validationRicMaxDrawdown
              validationFncV4Mean
              validationFncV4Std
              validationFncV4Sharpe
              validationFncV4MaxDrawdown
              validationCorrMeanRating
              validationCorrPlusMmcMean
              validationCorrPlusMmcMeanRating
              validationCorrPlusMmcSharpe
              validationCorrPlusMmcSharpeDiff
              validationCorrPlusMmcSharpeDiffRating
              validationCorrPlusMmcSharpeRating
              validationCorrPlusMmcStd
              validationCorrPlusMmcStdRating
              validationCorrSharpe
              validationCorrSharpeRating
              validationCorrStd
              validationCorrStdRating
              validationFeatureCorrMax
              validationFeatureCorrMaxRating
              validationFeatureNeutralCorrMean
              validationFeatureNeutralCorrMeanRating
              validationFeatureNeutralCorrV3Mean
              validationFeatureNeutralCorrV3MeanRating
              validationBmcMean
              validationMaxDrawdown
              validationMaxDrawdownRating
              validationMmcMean
              validationMmcMeanRating
              validationMmcSharpe
              validationMmcSharpeRating
              validationMmcStd
              validationMmcStdRating
              validationApy
              validationAutocorr
              validationAdjustedSharpe
              validationChurnMean
              validationChurnStd
              validationChurnMax
            }
        }
    }
  `
  const res = await _post(query, { id: id, modelId: modelId })

  return res.diagnostics
}

async function getDiagnosticsLogs(pickleId) {
  const query = `
    query getDiagnosticsTriggerLogs($pickleId: ID!) {
      diagnosticsTriggerLogs(pickleId: $pickleId) {
        message
        timestamp
      }
    }
  `
  const res = await _post(query, { pickleId })
  return res.diagnosticsTriggerLogs
}

async function deleteDiagnostics(diagnosticsIds) {
  const query = `
    mutation deleteDiagnostics($diagnosticsIds: [String!]) {
      deleteDiagnostics(v2DiagnosticsIds: $diagnosticsIds)
    }
  `
  const res = await _post(query, { diagnosticsIds: diagnosticsIds })
  return res.unreadBanners
}

async function getMaxModelSlots(tournament) {
  const query = `
    query getMaxModelSlots($tournament: Int!) {
      account {
        maxModelSlots(tournament: $tournament)
      }
    }
  `
  const res = await _post(query, { tournament: tournament })
  return res.account.maxModelSlots
}

async function getTotalStakeValue(tournament) {
  const query = `
    query getTotalStakeValue($tournament: Int!) {
      account {
        totalStakeValues(tournament: $tournament) {
            time
            value
        }
      }
    }
  `
  const res = await _post(query, { tournament: tournament })
  return res.account.totalStakeValues
}

async function getAccountNmrReturns(tournament) {
  const query = `
    query getAccountNmrReturns($tournament: Int!) {
      account {
        returns(tournament: $tournament) {
          oneDay
          threeMonths
          oneYear
          allTime
        }
      }
    }
  `
  const res = await _post(query, { tournament: tournament })
  return res.account
}

async function getRoundDetails(tournament, roundNumber) {
  const query = `
    query roundDetails($tournament: Int!, $roundNumber: Int!){
      roundDetails(tournament: $tournament, roundNumber: $roundNumber) {
        models {
          selectedStakeValue
          modelName
          id
          computeEnabled
          team
          payoutPending
          payoutSettled
          profileUrl
          
          # signals and numerai
          tc
          tcPercentile
          
          # numerai only
          corrWMetaModel
          fnc
          corr20: corr_20
          v2_corr20: v2Corr20
          corr60: corr_60
          mmc60: mmc_60
          cort20
          fnc_v3: fncV3
          mcwnm
          apcwnm
          mmc
          mmcPercentile
          bmc
          
          # signals only
          corr
          corr_v4: corrV4
          ric
          fnc_v4: fncV4
          ic_v2: icV2
          cwsnmm
          mcwsm
          apcwsm
          alpha
          mpc
        }
        status
        openTime
        closeTime
        closeStakingTime
        scoresUpdatedTime
        roundNumber
        roundTarget
        roundId
        tournament
        totalPayout
        totalEarned
        totalBurned
        totalAtStake
        totalStakes
        roundResolveTime
        payoutFactor
        allHistogramData{
          bins
          counts
        }
        stakedHistogramData{
          bins
          counts
        }
      }
    }`
  const res = await _post(query, { tournament, roundNumber })
  return res.roundDetails
}

// Round details without models array and histogram data
async function getRoundDetailsSparse(tournament, roundNumber) {
  const query = `
    query roundDetailsSparse($tournament: Int!, $roundNumber: Int!){
      roundDetails(tournament: $tournament, roundNumber: $roundNumber) {
        status
        openTime
        closeStakingTime
        closeTime
        scoreTime
        scoresUpdatedTime
        roundNumber
        roundTarget
        roundId
        tournament
        roundResolveTime
        roundResolved
        payoutFactor
      }
    }`
  const res = await _post(query, { tournament, roundNumber })
  return res.roundDetails
}

async function getModelUploadAuth({ modelId, filename }) {
  const query = `query getComputePickleUploadAuth($modelId: String!, $filename: String!) {
    computePickleUploadAuth(modelId: $modelId, filename: $filename) {
      filename
      url
    }
  }`
  const res = await _post(query, { modelId, filename })
  return res.computePickleUploadAuth
}

async function createModelUpload({
  filename,
  modelId,
  dataVersionId,
  dockerImageId,
  source,
}) {
  const query = `mutation createModelUpload ($filename: String!, $modelId: ID, $dataVersionId: ID, $dockerImageId: ID, $tournament: Int!, $source: String) {
    createComputePickleUpload(filename: $filename, modelId: $modelId, dataVersionId: $dataVersionId, dockerImageId: $dockerImageId, tournament: $tournament, source: $source) {
     id
     modelId
     dataVersionId
     dockerImageId
     insertedAt
     triggerStatus
     validationStatus
     version
    }
  }`
  const res = await _post(query, {
    filename,
    modelId,
    dataVersionId,
    dockerImageId,
    tournament: 8,
    source,
  })
  return res
}

async function assignPickleToModel({ pickleId, modelId }) {
  const query = `mutation assignPickleToModel($pickleId: ID, $modelId: String) {
    assignPickleToModel(pickleId: $pickleId, modelId: $modelId)
  }`
  // Don't spread pickleId so we can set pickleId to null (disabling)
  const res = await _post(query, { pickleId: pickleId, modelId })
  return res.assignPickleToModel
}

async function getModelUploads({
  pickleId,
  modelId,
  unassigned,
  includeTriggerStatuses,
}) {
  const query = includeTriggerStatuses
    ? `query computePickles($id: ID, $modelId: ID, $unassigned: Boolean! = false) {
    computePickles(id: $id, modelId: $modelId, unassigned: $unassigned) {
      id
      modelId
      dataVersion
      dataVersionId
      dockerImage
      dockerImageId
      dockerImageName
      diagnosticsId
      diagnosticsStatus
      diagnosticsStatusDescription
      insertedAt
      updatedAt
      filename
      version
      validationStatus
      triggerStatus
      triggers {
        status
        id
        insertedAt
        statuses {
          id
          description
          status
          insertedAt
        }
      }
    }
  }`
    : `query computePickles($id: ID, $modelId: ID, $unassigned: Boolean! = false) {
    computePickles(id: $id, modelId: $modelId, unassigned: $unassigned) {
      id
      modelId
      dataVersion
      dataVersionId
      dockerImage
      dockerImageId
      dockerImageName
      diagnosticsId
      diagnosticsStatus
      diagnosticsStatusDescription
      insertedAt
      updatedAt
      filename
      version
      validationStatus
      triggerStatus
    }
  }`
  const res = await _post(query, { id: pickleId, modelId, unassigned })
  return res.computePickles
}

async function getComputePickleDockerImages() {
  const query = `query getComputePickleDockerImages {
    computePickleDockerImages {
      default
      deprecated
      experimental
      name
      tag
      id
    }
  }`
  const res = await _post(query)
  return res.computePickleDockerImages
}

async function getComputePickleDataVersions() {
  const query = `query getComputePickleDataVersions {
    computePickleDataVersions {
      default
      id
      name
      type
      version
      experimental
    }
  }`
  const res = await _post(query)
  return res.computePickleDataVersions
}

async function getTriggerLogs({ triggerId }) {
  const query = `query triggerLogs($triggerId: ID) {
    triggerLogs(triggerId: $triggerId) {
      message
      timestamp
    }
  }`
  const res = await _post(query, { triggerId })
  return res.triggerLogs
}

async function getSubmissionAuth({ filename, modelId, tournament }) {
  const query = `
    query getSubmissionAuth($filename: String!, $tournament: Int!, $modelId: ID) {
      submission_upload_auth(filename: $filename, tournament: $tournament, modelId: $modelId) {
          filename
          url
      }
    }
  `
  const res = await _post(query, { filename, tournament, modelId })
  return res.submission_upload_auth
}

async function getSubmissionDownloadAuth(id) {
  const query = `
    query getSubmissionDownloadAuth($id: ID!) {
      submission_download_auth(id: $id) {
          filename
          url
      }
    }
  `
  const res = await _post(query, { id })
  return res.submission_download_auth
}

async function getDiagnosticsUploadAuthSignals({ filename, modelId }) {
  const tournament = 11
  const query = `
    query getDiagnosticsAuth($filename: String!, $modelId: ID, $tournament: Int!) {
      diagnosticsUploadAuth(filename: $filename, modelId: $modelId, tournament: $tournament) {
          filename
          url
      }
    }
  `
  const res = await _post(query, {
    filename,
    modelId: modelId,
    tournament: tournament,
  })
  return res.diagnosticsUploadAuth
}

async function getDiagnosticsUploadAuth({ filename, modelId }) {
  const tournament = 8
  const query = `
    query getDiagnosticsAuth($filename: String!, $modelId: ID, $tournament: Int!) {
      diagnosticsUploadAuth(filename: $filename, modelId: $modelId, tournament: $tournament) {
          filename
          url
      }
    }
  `
  const res = await _post(query, {
    filename,
    modelId: modelId,
    tournament: tournament,
  })
  return res.diagnosticsUploadAuth
}

async function getProfileUploadAuth({ filename, modelId }) {
  const query = `
    query getProfileUploadAuth($filename: String!, $modelId: String) {
      profile_image_upload_auth(filename: $filename, modelId: $modelId) {
          filename
          url
      }
    }
  `
  const res = await _post(query, { filename, modelId: modelId })
  return res.profile_image_upload_auth
}

async function getAccountProfileImageUploadAuth({ filename }) {
  const query = `
    query getAccountProfileImageUploadAuth($filename: String!) {
      accountProfileImageUploadAuth(filename: $filename) {
        filename
        url
      }
    }
  `
  const res = await _post(query, { filename })
  return res.accountProfileImageUploadAuth
}

async function getEarnProgress(tournament) {
  const query = `
    query getEarnQuestsProgress($tournament: Int!) {
      earnQuestsProgress(tournament: $tournament) {
          firstStake
          firstSubmission
          firstDiagnostics
          madeFiveModels
          stakeSubmittedFourWeeks {
            complete
            status
          }
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.earnQuestsProgress
}

async function getNFTeeContract(network) {
  const query = `
    query nftContract($network: String!) {
      nfteeContract (network: $network) {
       address
      }
    }
  `
  const res = await _post(query, { network })
  return res.nfteeContract
}

async function getNFTeeData(network) {
  const query = `
    query getNFTeeData($network: String!) {
      nftee (network: $network) {
        nftees {
          tokenId
        }
        voucher {
          secret
        }
      }
    }
  `
  const res = await _post(query, { network })
  return res.nftee
}

async function getNFTeeVoucher(
  contractNetwork,
  minterAddress,
  secret,
  tokenIds,
) {
  const query = `
    query getNFTeeVoucher($contractNetwork: String!, $minterAddress: String!, $secret: String!) {
      nfteeVoucher (contractNetwork: $contractNetwork, minterAddress: $minterAddress, secret: $secret) {
        signature
        tokenIds
      }
    }
  `
  const res = await _post(query, {
    contractNetwork,
    minterAddress,
    secret,
    tokenIds,
  })
  return res.nfteeVoucher
}

async function getNFTeeDataWithSecret(network, secret) {
  const query = `
    query getNFTeeDataWithSecret($network: String!, $secret: String!) {
      nfteeWithSecret(network: $network, secret: $secret) {
        nftees {
          tokenId
        }
        voucher {
          secret
        }
      }
    }
  `
  const res = await _post(query, { network, secret })
  return res.nfteeWithSecret
}

async function getNFTeeDataWithAddress(network, walletAddress) {
  const query = `
    query getNFTeeDataWithAddress($network: String!, $walletAddress: String!) {
      nfteeWithAddress(network: $network, walletAddress: $walletAddress) {
        nftees {
          tokenId
        }
        voucher {
          secret
        }
      }
    }
  `
  const res = await _post(query, { network, walletAddress })
  return res.nfteeWithAddress
}

async function createSubmission({
  filename,
  modelId,
  tournament,
  version = 2,
}) {
  const query = `
    mutation createSubmission($filename: String!, $tournament: Int!, $modelId: ID, $version: Int!, $source: String!) {
      createSubmission(filename: $filename, tournament: $tournament, modelId: $modelId, version: $version, source: $source) {
        id
      }
    }
  `
  await _post(query, {
    filename,
    tournament,
    modelId,
    version,
    source: 'website',
  })
}

async function createDiagnostics({ filename, modelId }) {
  const tournament = 8
  const query = `
    mutation createDiagnostics($filename: String!, $modelId: ID, $tournament: Int!) {
      createDiagnostics(filename: $filename, modelId: $modelId, tournament: $tournament) {
        id
      }
    }
  `
  await _post(query, { filename, modelId, tournament })
}

async function createDiagnosticsSignals({ filename, modelId }) {
  const tournament = 11
  const query = `
    mutation createDiagnostics($filename: String!, $modelId: ID, $tournament: Int!) {
      createDiagnostics(filename: $filename, modelId: $modelId, tournament: $tournament) {
        id
      }
    }
  `
  await _post(query, { filename, modelId: modelId, tournament: tournament })
}

async function changeEmailAddress({ newEmail, password, code }) {
  const query = `
    mutation changeEmailAddress($code: String, $password: String!, $newEmail: String!) {
      changeEmail(code: $code, password: $password, newEmail: $newEmail)
    }
  `
  await _post(query, { newEmail, password, code })
}

async function changePassword({ password, newPassword, code }) {
  const query = `
    mutation changePassword($code: String, $password: String!, $newPassword: String!) {
      changePassword(code: $code, password: $password, newPassword: $newPassword) {
        token
        username
      }
    }
  `
  const res = await _post(query, { password, newPassword, code })
  return res.changePassword
}

async function enableMFA({ password, code }) {
  const query = `
    mutation enableMFA($code: String!, $password: String!) {
      mfaEnable(code: $code, password: $password) {
        token
        username
      }
    }
  `
  const res = await _post(query, { password, code })
  return res.mfaEnable
}

async function disableMFA({ password, code }) {
  const query = `
    mutation disableMFA($code: String!, $password: String!) {
      mfaDisable(code: $code, password: $password)
    }
  `
  const res = await _post(query, { password, code })
  return res.mfaDisable
}

async function genQR() {
  const query = `
    mutation genQR {
      mfaQr {
        image
        recovery
        secret
      }
    }
  `
  const res = await _post(query)
  return res.mfaQr
}

async function changeStake({ value, type, modelId, tournamentNumber }) {
  const query = `
    mutation changeStake ($value: String!, $type: String!, $modelId: String, $tournamentNumber: Int) {
      v2ChangeStake(value: $value, type: $type, modelId: $modelId, tournamentNumber: $tournamentNumber) {
        dueDate
        requestedAmount
        status
        type
      }
    }
  `
  const res = await _post(query, { value, type, modelId, tournamentNumber })
  return res
}

async function increaseStake({ amount, modelId }) {
  const query = `
    mutation increaseStake ($amount: String!, $modelId: String) {
      increaseStake(amount: $amount, modelId: $modelId) {
        dueDate
        requestedAmount
        status
        type
      }
    }
  `
  const res = await _post(query, { amount, modelId })
  return res
}

async function releaseStake({ amount, modelId, drain }) {
  const query = `
    mutation releaseStake ($amount: String!, $modelId: String, $drain: Boolean) {
      releaseStake(amount: $amount, modelId: $modelId, drain: $drain) {
        dueDate
        requestedAmount
        status
        type
        drain
      }
    }
  `
  const res = await _post(query, { amount, modelId, drain })
  return res
}

async function cancelPendingStakeRelease(modelId) {
  const query = `
    mutation cancelPendingStakeRelease($modelId: String) {
      cancelPendingStakeRelease(modelId: $modelId) {
        status
      }
    }
  `
  const res = await _post(query, { modelId })
  return res
}

async function changePayoutSelection({ modelId, tournament, takeProfit }) {
  const res = await _post(
    `mutation changePayoutSelection (
      $modelId: String,
      $tournament: Int,
      $takeProfit: Boolean
    ) {
      v3_change_payout_selection(
        modelId: $modelId,
        tournamentNumber: $tournament,
        takeProfit: $takeProfit
      )
    }`,
    {
      modelId,
      tournament,
      takeProfit,
    },
  )
  return res
}

async function getAccountLeaderboard({
  limit,
  offset,
  orderBy,
  direction,
  filterBy,
  tournament,
}) {
  const query = `
    query getAccountLeaderboard($limit: Int, $offset: Int, $orderBy: String, $direction: String, $filterBy: String, $tournament: Int) {
      accountLeaderboard(limit: $limit, offset: $offset, orderBy: $orderBy, direction: $direction, filterBy: $filterBy, tournament: $tournament) {
        username
        title
        displayName
        profileUrl
        rank
        mmc: mmc
        corr20V2: v2Corr20
        corr60: corr_60
        mmc60: mmc_60
        corrV4: corrV4
        fncV4: fncV4
        icV2: icV2
        ric: ric
        alpha: alpha
        mpc
        corr: corr
        nmrStaked
        return3m
        return1y
        returnAllTime
        team
      }
    }
  `
  return await _post(
    query,
    {
      limit,
      offset,
      orderBy,
      direction,
      filterBy,
      tournament,
    },
    'accountLeaderboard',
  )
}

async function getLeaderboard({ limit, offset, orderBy, direction, filterBy }) {
  const query = `
    query getLeaderboard($limit: Int, $offset: Int, $orderBy: String, $direction: String, $filterBy: String) {
      v2Leaderboard(limit: $limit, offset: $offset, orderBy: $orderBy, direction: $direction, filterBy: $filterBy) {
        username
        mmcRep
        corr20Rep
        corr20V2Rep
        corr60Rep
        mmc60Rep
        return1y: return52Weeks
        return3m: return13Weeks
        nmrStaked
        return1d: return1Day
        rank
        profileUrl
        team
        is_active
      }
    }
  `
  return await _post(
    query,
    {
      limit,
      offset,
      orderBy,
      direction,
      filterBy,
    },
    'v2Leaderboard',
  )
}

async function getSignalsLeaderboard({
  limit,
  offset,
  orderBy,
  direction,
  filterBy,
}) {
  var query = `
    query getSignalsLeaderboard($limit: Int, $offset: Int, $orderBy: String, $direction: String, $filterBy: String) {
      signalsLeaderboard(limit: $limit, offset: $offset, orderBy: $orderBy, direction: $direction, filterBy: $filterBy) {
        username
        mmcRep
        fncV4Rep
        icV2Rep
        ricRep
        alphaRep
        mpcRep
        corrV4Rep
        nmrStaked
        return1d: return1Day
        profileUrl
        return3m: return13Weeks
        return1y: return52Weeks
        rank
        is_active
        
      }
    }
  `
  return await _post(
    query,
    {
      limit,
      offset,
      orderBy,
      direction,
      filterBy,
    },
    'signalsLeaderboard',
  )
}

async function getCryptoSignalsLeaderboard({
  limit,
  offset,
  orderBy,
  direction,
  filterBy,
}) {
  var query = `
    query getCryptoSignalsLeaderboard($limit: Int, $offset: Int, $orderBy: String, $direction: String, $filterBy: String) {
      cryptosignalsLeaderboard(limit: $limit, offset: $offset, orderBy: $orderBy, direction: $direction, filterBy: $filterBy) {
        username
        rank
        corrRep
        mmcRep
        nmrStaked
        return1d: return1Day
        return3m: return13Weeks
        return1y: return52Weeks
        profileUrl
        is_active
      }
    }
  `
  return await _post(
    query,
    {
      limit,
      offset,
      orderBy,
      direction,
      filterBy,
    },
    'cryptosignalsLeaderboard',
  )
}

async function getCurrentRoundInfo(tournament) {
  const query = `
    query getCurrentRoundInfo($tournament: Int) {
      rounds(limit:1, status: OPEN, tournament: $tournament) {
        number
        openTime
        scoreTime
        closeStakingTime
        closeTime
        numTickers
        numValidationEras
        numValidationTickers
        payoutFactor
        stakeThreshold
        minMmcMultiplier
        maxMmcMultiplier
        minCorrMultiplier
        maxCorrMultiplier
        minTcMultiplier
        maxTcMultiplier
        defaultMmcMultiplier
        defaultCorrMultiplier
        defaultTcMultiplier
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.rounds[0]
}

async function getLatestRoundInfo(tournament) {
  const query = `
    query getLatestRoundInfo($tournament: Int) {
      rounds(limit:1, tournament: $tournament) {
        number
        openTime
        scoreTime
        closeStakingTime
        closeTime
        numTickers
        numValidationEras
        numValidationTickers
        payoutFactor
        stakeThreshold
        minMmcMultiplier
        maxMmcMultiplier
        minCorrMultiplier
        maxCorrMultiplier
        minTcMultiplier
        maxTcMultiplier
        defaultMmcMultiplier
        defaultCorrMultiplier
        defaultTcMultiplier
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.rounds[0]
}

async function changeSubmissionWebhook({ newSubmissionWebhook, modelId }) {
  const query = `
    mutation changeSubmissionWebhook($newSubmissionWebhook: String, $modelId: String!) {
      setSubmissionWebhook(
        newSubmissionWebhook: $newSubmissionWebhook,
        modelId: $modelId
      )
    }`
  const res = await _post(query, { newSubmissionWebhook, modelId })
  return res
}

async function triggerModelWebhook({ modelId }) {
  const query = `
    mutation testSubmissionWebhook($modelId: String!) {
      triggerModelWebhook(modelId: $modelId)
    }`
  const res = await _post(query, { modelId })
  return res.triggerModelWebhook
}

async function setComputeWeekdayEnabled({
  modelId = null,
  dailyTriggerEnabled = false,
}) {
  const query = `
    mutation setComputeWeekdayEnabled($modelId: String!, $dailyTriggerEnabled: Boolean!) {
      setComputeWeekdayEnabled(
        modelId: $modelId,
        computeWeekdayEnabled: $dailyTriggerEnabled
      )
    }`
  const res = await _post(query, { modelId, dailyTriggerEnabled })
  return res.setComputeWeekdayEnabled
}

async function getApiTokenScopes() {
  const query = `
    query getApiTokenScopes {
      apiTokenScopes {
        description
        name
      }
    }`
  const res = await _post(query)
  return res.apiTokenScopes
}

async function createApiToken({ code, name, password, selectedApiScopes }) {
  const query = `
    mutation createApiToken($code: String, $name: String!, $password: String!, $scopes: [String]!) {
      createApiToken(code: $code, password: $password, name: $name, scopes: $scopes){
        name
        scopes
        publicId
        secretKey
      }
    }
  `
  const res = await _post(query, {
    code,
    name,
    password,
    scopes: selectedApiScopes,
  })
  return res
}

async function revokeApiToken({ publicId }) {
  const query = `
    mutation revokeApiToken($publicId: String!) {
      revokeApiToken(publicId: $publicId){
        name
      }
    }
  `
  const res = await _post(query, { publicId })
  return res
}

async function getTournamentOverview(tournament) {
  var query = `
    query getTournamentOverview ($tournament: Int!) {
      v2TournamentOverview (tournament: $tournament) {
        totalStakes
        totalAtStake
        totalAtRisk
        stakedSubmissions
        totalAccounts
        stakedModels {
          count
          date
        }
        stakedAccounts {
          count
          date
        }
        returns {
          oneYear
        }
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.v2TournamentOverview
}

async function getTournamentTotalAccounts(tournament) {
  var query = `
    query getTournamentTotalAccounts($tournament: Int!) {
      v2TournamentOverview(tournament: $tournament) {
        totalAccounts
      }
    }
  `
  const res = await _post(query, { tournament })
  return res.v2TournamentOverview.totalAccounts
}

async function markNotificationAsRead({ notificationId, modelId }) {
  const query = `
    mutation markNotificationAsRead($notificationId: String!, $modelId: String) {
      markNotificationAsRead(notificationId: $notificationId, modelId: $modelId)
    }
  `
  const res = await _post(query, { notificationId, modelId })
  return res
}

async function v2WithdrawNmr({ code, password, toAddress, nmrAmount }) {
  const query = `
    mutation v2WithdrawNmr($code: String, $password: String!, $toAddress: String!, $nmrAmount: String!) {
      v2WithdrawNmr(code: $code, password: $password, toAddress: $toAddress, nmrAmount: $nmrAmount) {
        status
        txHash
      }
    }
  `
  const res = await _post(query, {
    code,
    password,
    toAddress,
    nmrAmount,
  })
  return res.v2WithdrawNmr
}

async function setUserBio({ value, modelId }) {
  const query = `
    mutation setUserBio($value: String!, $modelId: String) {
      setUserBio(value: $value, modelId: $modelId)
    }
  `
  const res = await _post(query, { value, modelId: modelId })
  return res.setUserBio
}

async function setUserLink({ text, url, modelId }) {
  const query = `
    mutation setUserLink($text: String!, $url: String!, $modelId: String) {
      setUserLink(linkText: $text, linkUrl: $url, modelId: $modelId)
      }
    `
  const res = await _post(query, { text, url, modelId: modelId })
  return res.setUserLink
}

async function setIsBeta(enabled) {
  const query = `
    mutation setIsBeta($enabled: Boolean!) {
      setIsBeta(enabled: $enabled)
    }
  `
  const res = await _post(query, { enabled })
  return res.setIsBeta
}

async function setEmailPreferences(preferences) {
  const query = `
    mutation setEmailPreferences(
        $compute: Boolean!,
        $deposit: Boolean!,
        $diagnostics: Boolean!,
        $modelUploadReceipt: Boolean!,
        $pickleRoundOpen: Boolean!,
        $pickleRoundStatus: Boolean!,
        $roundOpen: Boolean!,
        $roundSummary: Boolean!,
        $signalsRoundOpen: Boolean!,
        $signalsRoundSummary: Boolean!,
        $signalsSubmission: Boolean!,
        $cryptoRoundOpen: Boolean!,
        $cryptoRoundSummary: Boolean!,
        $cryptoSubmission: Boolean!,
        $stakeChange: Boolean!,
        $submission: Boolean!,
        $submissionSuccess: Boolean!,
        $submissionsStatus: Boolean!,
        $withdrawal: Boolean!) {
    updateEmailPreferences(
        compute: $compute,
        deposit: $deposit,
        diagnostics: $diagnostics,
        modelUploadReceipt: $modelUploadReceipt,
        pickleRoundOpen: $pickleRoundOpen,
        pickleRoundStatus: $pickleRoundStatus,
        roundOpen: $roundOpen,
        roundSummary: $roundSummary,
        signalsRoundOpen: $signalsRoundOpen,
        signalsRoundSummary: $signalsRoundSummary,
        signalsSubmission: $signalsSubmission,
        cryptoRoundOpen: $cryptoRoundOpen,
        cryptoRoundSummary: $cryptoRoundSummary,
        cryptoSubmission: $cryptoSubmission,
        stakeChange: $stakeChange,
        submission: $submission,
        submissionSuccess: $submissionSuccess,
        submissionsStatus: $submissionsStatus,
        withdrawal: $withdrawal)
    }
  `
  const res = await _post(query, preferences)
  return res.updateEmailPreferences
}

async function getEmailPreferencesByToken(token) {
  const query = `
    query getEmailPreferencesByToken($updateToken: String!) {
      emailPreferences(updateToken: $updateToken) {
        compute
        deposit
        diagnostics
        modelUploadReceipt
        pickleRoundOpen
        pickleRoundStatus
        roundOpen
        roundSummary
        signalsRoundOpen
        signalsRoundSummary
        signalsSubmission
        cryptoRoundOpen
        cryptoRoundSummary
        cryptoSubmission
        stakeChange
        submission
        submissionSuccess
        submissionsStatus
        withdrawal
      }
    }
    `
  const res = await _post(query, { updateToken: token })
  return res.emailPreferences
}

async function setEmailPreferencesWithToken({ preferences, token }) {
  const query = `
    mutation setEmailPreferencesWithToken(
      $updateToken: String!,
      $compute: Boolean!,
      $deposit: Boolean!,
      $diagnostics: Boolean!,
      $modelUploadReceipt: Boolean!,
      $pickleRoundOpen: Boolean!,
      $pickleRoundStatus: Boolean!,
      $roundOpen: Boolean!,
      $roundSummary: Boolean!,
      $signalsRoundOpen: Boolean!,
      $signalsRoundSummary: Boolean!,
      $signalsSubmission: Boolean!,
      $cryptoRoundOpen: Boolean!,
      $cryptoRoundSummary: Boolean!,
      $cryptoSubmission: Boolean!,
      $stakeChange: Boolean!,
      $submission: Boolean!,
      $submissionSuccess: Boolean!,
      $submissionsStatus: Boolean!,
      $withdrawal: Boolean!) {
    updateEmailPreferencesWithToken(
      updateToken: $updateToken,
      compute: $compute,
      deposit: $deposit,
      diagnostics: $diagnostics,
      modelUploadReceipt: $modelUploadReceipt,
      pickleRoundOpen: $pickleRoundOpen,
      pickleRoundStatus: $pickleRoundStatus,
      roundOpen: $roundOpen,
      roundSummary: $roundSummary,
      signalsRoundOpen: $signalsRoundOpen,
      signalsRoundSummary: $signalsRoundSummary,
      signalsSubmission: $signalsSubmission,
      cryptoRoundOpen: $cryptoRoundOpen,
      cryptoRoundSummary: $cryptoRoundSummary,
      cryptoSubmission: $cryptoSubmission,
      stakeChange: $stakeChange,
      submission: $submission,
      submissionSuccess: $submissionSuccess,
      submissionsStatus: $submissionsStatus,
      withdrawal: $withdrawal)
    }
  `
  const res = await _post(query, { ...preferences, updateToken: token })
  return res.updateEmailPreferencesWithToken
}

async function pipelineStatus(tournament) {
  const query = `
  query($tournament: String!, $date: String!) {
    pipelineStatus(tournament: $tournament, date: $date) {
      isScoringDay
      startP90Eta
      startedAt
      dataP90Eta
      dataP99Eta
      dataReadyAt
      scoreP90Eta
      scoreP99Eta
      scoredAt
      resolveP90Eta
      resolveP99Eta
      resolvedAt
      nextStartP90Eta
    }
  }
  `
  const date = new Date()
  const pacific_tz_offset = -8

  let local_date = new Date(date)
  local_date.setHours(local_date.getHours() + pacific_tz_offset)
  local_date = local_date.toISOString().split('T')[0]

  const res = await _post(query, {
    tournament,
    date: local_date,
  })
  return res.pipelineStatus
}

async function getComputeLambdaData() {
  const query = `
  query getFunctionData {
    computeLambdaData {
      lambdaFunctionName
      lambdaRoleArn
      modelName
      userId
      lambdaLastInvoked
      lambdaLastUpdated
      dataVersion
      numerapiVersion
      isComputeWeekdayEnabled
      status {
        status
        description
      }
    }
  }
  `
  const res = await _post(query)
  return res.computeLambdaData
}

async function invokeModelLambda(modelId, invocationType) {
  const query = `
  mutation invokeModelLambda($modelId: String!, $invocationType: String!) {
    invokeModelLambda(modelId: $modelId, invocationType: $invocationType)
  }
  `
  const res = await _post(query, { modelId, invocationType })
  return res.invokeModelLambda
}

async function invocationStatus(requestId) {
  const query = `
  query invocationStatus($requestId: String!) {
    invocationStatus(requestId: $requestId) {
      insertedAt
      status
    }
  }
  `
  const res = await _post(query, { requestId })
  return res.invocationStatus
}

async function invocations(modelId) {
  const query = `
  query invocations($modelId: String!) {
    invocations(modelId: $modelId) {
      requestId
      logStreamName
      insertedAt
      status
    }
  }
  `
  const res = await _post(query, { modelId })
  return res.invocations
}

async function invocationLogs(modelId, logStreamName) {
  const query = `
  query invocationLogs($modelId: String!, $logStreamName: String!) {
    invocationLogs(modelId: $modelId, logStreamName: $logStreamName) {
      timestamp
      message
    }
  }
  `
  const res = await _post(query, {
    modelId: modelId,
    logStreamName: logStreamName,
  })
  return res.invocationLogs
}

async function v2RoundModelPerformances({
  modelId = null,
  roundNumber__eq = null,
  roundNumber__gte = null,
  lastNRounds = null,
  distinctOnRound = false,
  tournament = 8,
  submittedOnly = false,
  resolvedOnly = false,
  resolvedWithinLastNDays = null,
  scoredWithinLastNDays = null,
}) {
  const query = `
  query v2RoundModelPerformances($modelId: String, $roundNumber__eq: Int, $roundNumber__gte: Int, $tournament: Int, $distinctOnRound: Boolean, $lastNRounds: Int, $submittedOnly: Boolean, $resolvedOnly: Boolean, $resolvedWithinLastNDays: Int, $scoredWithinLastNDays: Int) {
    v2RoundModelPerformances(
        modelId: $modelId,
        roundNumber__eq: $roundNumber__eq,
        roundNumber__gte: $roundNumber__gte,
        lastNRounds: $lastNRounds,
        distinctOnRound: $distinctOnRound,
        tournament: $tournament,
        submittedOnly: $submittedOnly,
        resolvedOnly: $resolvedOnly,
        resolvedWithinLastNDays: $resolvedWithinLastNDays,
        scoredWithinLastNDays: $scoredWithinLastNDays
      ) {
      roundNumber
      roundResolveTime
      roundCloseStakingTime
      roundDataDatestamp
      roundPayoutFactor
      roundScoreTime
      roundResolved
      roundTarget
      atRisk
      corrMultiplier
      mmcMultiplier
      tcMultiplier
      churnThreshold
      prevWeekChurnMax
      turnoverThreshold
      prevWeekTurnoverMax
      submissionScores {
        date
        day
        resolveDate
        resolved
        displayName
        value
        percentile
        payoutPending
        payoutSettled
      }
    }
  }
  `
  const res = await _post(query, {
    modelId: modelId,
    roundNumber__eq: roundNumber__eq,
    roundNumber__gte: roundNumber__gte,
    lastNRounds: lastNRounds,
    distinctOnRound: distinctOnRound,
    tournament: tournament,
    submittedOnly: submittedOnly,
    resolvedOnly: resolvedOnly,
    resolvedWithinLastNDays: resolvedWithinLastNDays,
    scoredWithinLastNDays: scoredWithinLastNDays,
  })
  return res.v2RoundModelPerformances
}

async function v2IntraRoundModelPerformances({
  modelId = null,
  roundNumber__eq = null,
  roundNumber__gte = null,
  distinctOnRound = false,
  tournament = 8,
}) {
  const query = `
  query v2IntraRoundModelPerformances($modelId: String, $roundNumber__eq: Int, $roundNumber__gte: Int, $tournament: Int, $distinctOnRound: Boolean) {
    v2RoundModelPerformances(
        modelId: $modelId,
        roundNumber__eq: $roundNumber__eq,
        roundNumber__gte: $roundNumber__gte,
        distinctOnRound: $distinctOnRound
        tournament: $tournament) {
      roundNumber
      roundResolveTime
      roundPayoutFactor
      roundScoreTime
      roundResolved
      roundTarget
      atRisk
      corrMultiplier
      mmcMultiplier
      tcMultiplier
      intraRoundSubmissionScores {
        displayName
        value
        percentile
        day
        date
        payoutPending
        payoutSettled
      }
    }
  }
  `
  const res = await _post(query, {
    modelId: modelId,
    roundNumber__eq: roundNumber__eq,
    roundNumber__gte: roundNumber__gte,
    distinctOnRound: distinctOnRound,
    tournament: tournament,
  })
  return res.v2RoundModelPerformances
}

async function userScores(modelId) {
  const query = `
  query userScores($modelId: String) {
    userScores(modelId: $modelId) {
      date
      displayName
      rank
      reputation
      stakedRank
    }
  }
  `
  const res = await _post(query, {
    modelId: modelId,
  })
  return res.userScores
}

async function removeDiscordAccountLink() {
  const query = `
    mutation removeDiscordAccountLink {
      removeDiscordAccount
    }
  `
  const res = await _post(query)
  return res
}

async function getMetaModelPage() {
  const query = `
  query getMetaModelPage {
    cryptosignalsMetaModelPage {
      tableData {
        symbol
        mmRank
        latestPrice
        value
        logo
      }
    }
  }
  `
  const res = await _post(query)
  return res.cryptosignalsMetaModelPage
}

async function subscribeToCryptoMM(email) {
  const query = `
    mutation subscribeToCryptoMM($email: String) {
      subscribeToCryptoMmMailingList(email: $email)
    }
  `
  const res = await _post(query, { email })
  return res.subscribeToCryptoMmMailingList
}

async function unsubscribeFromMailingList(list, unsubscribe_token) {
  const query = `
    mutation unsubscribeFromMailingList($list: String!, $unsubscribe_token: String!) {
      unsubscribeFromMailingList(list: $list, unsubscribe_token: $unsubscribe_token)
    }
  `
  const res = await _post(query, { list, unsubscribe_token })
  return res.unsubscribeFromMailingList
}

const client = {
  getAccount,
  getAccountProfile,
  getSeasonAccountSummary,
  getSeasonSummaryStatusData,
  getSeasonParticipants,
  getSeasonLeaderboard,
  updateAccountProfile,
  nameCheck,
  getTutorialCompletion,
  getV3UserProfile,
  getDataset,
  getOldDataset,
  getPrices,
  latestNmrPrice,
  getReport,
  getAccountLeaderboard,
  getLeaderboard,
  getSignalsLeaderboard,
  getCryptoSignalsLeaderboard,
  getCurrentRoundInfo,
  getLatestRoundInfo,
  getTournamentOverview,
  getDefaultApiToken,
  confirmSignup,
  confirmResetPassword,
  changeEmailAddress,
  login,
  logout,
  singleSignOn,
  signup,
  deleteAccount,
  resetPassword,
  resetEmailChange,
  verifyEmail,
  verifyLoginIp,
  getModelUploadAuth,
  createModelUpload,
  assignPickleToModel,
  getModelUploads,
  getComputePickleDataVersions,
  getComputePickleDockerImages,
  getTriggerLogs,
  getSubmissionAuth,
  getSubmissionDownloadAuth,
  createSubmission,
  changePassword,
  enableMFA,
  disableMFA,
  genQR,
  changeSubmissionWebhook,
  triggerModelWebhook,
  getApiTokenScopes,
  createApiToken,
  revokeApiToken,
  setOn401Callback,
  setGraphQLErrorCallback,
  markNotificationAsRead,
  v2WithdrawNmr,
  getProfileUploadAuth,
  getAccountProfileImageUploadAuth,
  setUserBio,
  setUserLink,
  pipelineStatus,
  changeStake,
  increaseStake,
  releaseStake,
  cancelPendingStakeRelease,
  changePayoutSelection,
  addModel,
  absorbAccount,
  setComputeWeekdayEnabled,
  setIsBeta,
  setEmailPreferences,
  getEmailPreferencesByToken,
  setEmailPreferencesWithToken,
  getSignalsSubmissionDiagnostics,
  getUnreadBanners,
  markBannerAsRead,
  getAccountWalletTransactions,
  getStakeTransactions,
  getRoundDetails,
  getAccountWalletPendingTransactions,
  getAccountWalletScheduledTransactions,
  getTotalStakeValue,
  getMaxModelSlots,
  getAccountNmrReturns,
  getRoundDetailsSparse,
  renameModel,
  renameAccount,
  modelNameAvailable,
  getDiagnostics,
  getDiagnosticsLogs,
  getDiagnosticsUploadAuthSignals,
  getDiagnosticsUploadAuth,
  deleteDiagnostics,
  createDiagnosticsSignals,
  createDiagnostics,
  listDatasets,
  getEarnProgress,
  getNFTeeContract,
  getNFTeeData,
  getNFTeeDataWithSecret,
  getNFTeeDataWithAddress,
  getNFTeeVoucher,
  getUnreadNotifications,
  dismissNotification,
  getComputeLambdaData,
  invokeModelLambda,
  invocationStatus,
  invocations,
  invocationLogs,
  v2RoundModelPerformances,
  v2IntraRoundModelPerformances,
  userScores,
  removeDiscordAccountLink,
  getMetaModelPage,
  getTournamentTotalAccounts,
  subscribeToCryptoMM,
  unsubscribeFromMailingList,
  BACKEND_URL,
}

// enable this.$client in vue components
Vue.prototype.$client = client

export default client
